import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Progress, Typography } from 'antd';

const HOLD_MS = 2500;
const TICK_MS = 50;

export const isShiftClosedError = (e: any): boolean =>
  e?.response?.status === 409 && e?.response?.data?.detail?.code === 'shift_not_open';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

// Барьер «кассовая смена закрыта» (1c.md §1 rev. 21; ServiceOrderPage №19 /
// OilChangePage №47): экстренный обход — кнопка срабатывает только по долгому
// удержанию 2.5 сек (защита от случайного нажатия).
const ShiftClosedModal: React.FC<Props> = ({ open, onClose, onConfirm }) => {
  const [progress, setProgress] = useState(0);
  const holdRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopHold = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    holdRef.current = 0;
    setProgress(0);
  }, []);

  const startHold = useCallback(() => {
    if (timerRef.current) return;
    holdRef.current = 0;
    timerRef.current = setInterval(() => {
      holdRef.current += TICK_MS;
      const pct = Math.min(100, Math.round((holdRef.current / HOLD_MS) * 100));
      setProgress(pct);
      if (holdRef.current >= HOLD_MS) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        holdRef.current = 0;
        setProgress(0);
        onConfirm();
      }
    }, TICK_MS);
  }, [onConfirm]);

  useEffect(() => {
    if (!open) stopHold();
    return stopHold;
  }, [open, stopHold]);

  return (
    <Modal
      open={open}
      title="Кассовая смена не открыта"
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      <Typography.Paragraph>
        Чек ККМ создаётся без привязки к смене и не будет виден в журнале чеков 1С
        с отбором «Текущая смена».
      </Typography.Paragraph>
      <Typography.Paragraph strong>
        Откройте смену в 1С и повторите сохранение.
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        Экстренный вариант: удерживайте кнопку ниже 2,5 секунды, чтобы сохранить
        без открытия смены.
      </Typography.Paragraph>
      <Button
        danger
        block
        onMouseDown={startHold}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={startHold}
        onTouchEnd={stopHold}
        onTouchCancel={stopHold}
      >
        Продолжить без открытия смены (удерживать)
      </Button>
      {progress > 0 && (
        <Progress percent={progress} showInfo={false} status="active" style={{ marginTop: 8 }} />
      )}
    </Modal>
  );
};

export default ShiftClosedModal;
